import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { Button, Icon, Label, Loader, Message } from "semantic-ui-react";

import { getBusinessRules } from "api/backendAPI";
import "./BusinessRules.scss";

export const BUSINESS_RULES_CONTRACT_VERSION = "1.0.0";

export function validateBusinessRulesResponse(response) {
  const payload = response && response.data;
  return response
    && response.businessRulesContractVersion === BUSINESS_RULES_CONTRACT_VERSION
    && payload
    && payload.schema_version === 1
    && payload.language === "fi"
    && Array.isArray(payload.rules)
    && payload.rules.every(rule => rule
      && typeof rule.rule_key === "string"
      && Number.isSafeInteger(rule.version)
      && rule.version > 0
      && ["active", "superseded"].includes(rule.status)
      && typeof rule.category === "string"
      && typeof rule.title_fi === "string"
      && typeof rule.rule_text_fi === "string"
      && rule.parameters && typeof rule.parameters === "object"
      && typeof rule.effective_at === "string"
      && typeof rule.source === "string"
      && typeof rule.evidence_ref === "string"
      && typeof rule.decided_by === "string"
      && typeof rule.created_at === "string");
}

function formatDate(value, language) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(language || "fi", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Helsinki",
  }).format(date);
}

export function BusinessRulesView({ rules, loading, error, onRefresh, t, language }) {
  const grouped = useMemo(() => rules.reduce((result, rule) => {
    const category = rule.category || "muu";
    if (!result[category]) result[category] = [];
    result[category].push(rule);
    return result;
  }, {}), [rules]);

  if (loading) return <Loader active inline="centered" content={t("businessRules.loading")} />;
  if (error) return (
    <Message negative data-testid="business-rules-error">
      <Message.Header>{t("businessRules.errorTitle")}</Message.Header>
      <p>{t(`businessRules.errors.${error}`)}</p>
      <Button basic onClick={onRefresh} icon="refresh" content={t("businessRules.refresh")} />
    </Message>
  );

  const activeCount = rules.filter(rule => rule.status === "active").length;
  return (
    <div data-testid="business-rules-list">
      <div className="businessRules__summary">
        <div><strong>{activeCount}</strong><span>{t("businessRules.activeRules")}</span></div>
        <div><strong>{Object.keys(grouped).length}</strong><span>{t("businessRules.categories")}</span></div>
        <div><Icon name="history" /><span>{t("businessRules.versioned")}</span></div>
      </div>
      {!rules.length ? <Message info content={t("businessRules.empty")} /> : Object.entries(grouped).map(([category, items]) => (
        <section className="businessRules__category" key={category}>
          <h2>{t(`businessRules.category.${category}`, { defaultValue: category })}</h2>
          <div className="businessRules__grid">
            {items.map(rule => (
              <article className={`businessRules__card businessRules__card--${rule.status}`} key={`${rule.rule_key}-${rule.version}`}>
                <div className="businessRules__cardHeader">
                  <div>
                    <p className="businessRules__key">{rule.rule_key}</p>
                    <h3>{rule.title_fi}</h3>
                  </div>
                  <Label basic color={rule.status === "active" ? "green" : "grey"}>
                    {t(`businessRules.status.${rule.status}`)} · v{rule.version}
                  </Label>
                </div>
                <p className="businessRules__text">{rule.rule_text_fi}</p>
                <details className="businessRules__audit">
                  <summary>{t("businessRules.auditTrail")}</summary>
                  <dl>
                    <div><dt>{t("businessRules.decidedBy")}</dt><dd>{rule.decided_by}</dd></div>
                    <div><dt>{t("businessRules.effectiveAt")}</dt><dd>{formatDate(rule.effective_at, language)}</dd></div>
                    <div><dt>{t("businessRules.source")}</dt><dd>{rule.source}</dd></div>
                    <div><dt>{t("businessRules.evidence")}</dt><dd>{rule.evidence_ref}</dd></div>
                  </dl>
                </details>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function BusinessRules() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ rules: [], loading: true, error: null, loadedAt: null });
  const load = useCallback(() => {
    setState(previous => ({ ...previous, loading: true, error: null }));
    getBusinessRules(true).then(response => {
      if (!validateBusinessRulesResponse(response)) {
        setState(previous => ({ ...previous, loading: false, error: "contract" }));
        return;
      }
      setState({ rules: response.data.rules, loading: false, error: null, loadedAt: new Date().toISOString() });
    }).catch(() => setState(previous => ({ ...previous, loading: false, error: "network" })));
  }, []);
  useEffect(load, [load]);

  return (
    <main className="pageShell businessRules">
      <Helmet title={t("businessRules.title")} />
      <div className="pageHeader businessRules__pageHeader">
        <div>
          <p className="pageHeader__eyebrow">FleetAdmin TMS · {t("businessRules.audit")}</p>
          <h1>{t("businessRules.title")}</h1>
          <p>{t("businessRules.lead")}</p>
        </div>
        <Button basic icon="refresh" content={t("businessRules.refresh")} onClick={load} />
      </div>
      {state.loadedAt && <p className="businessRules__loaded">{t("businessRules.loadedAt")}: {formatDate(state.loadedAt, i18n.language)}</p>}
      <BusinessRulesView {...state} onRefresh={load} t={t} language={i18n.language} />
    </main>
  );
}
